import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Container, Collapse, OverlayTrigger, Tooltip, Card, Alert, Badge, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import ContaServicoViewModel from '../../viewmodel/ContaServicoViewModel';
import { Perfil } from '../../model/Usuario/Perfil';
import api from '../../api';
import useUserStore from '../../stores/userStore';
import useUnidadeStore from '../../stores/unidadeStore';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';
import { BsArrowUp, BsQuestionCircle, BsShieldCheck } from 'react-icons/bs';
import Joyride, { CallBackProps, STATUS } from 'react-joyride';

const alteracaoPeriodicaDeSenhaImplementada = false;

const SolicitarContaServicoPage: React.FC = () => {
  const usuario = useUserStore((state) => state.usuario);
  const { unidades, loading: loadingUnidades, fetchUnidades } = useUnidadeStore();
  const [enviandoSolicitacao, setEnviandoSolicitacao] = useState(false);
  const [solicitacaoEnviada, setSolicitacaoEnviada] = useState(false);
  const [etapa, setEtapa] = useState<1 | 2>(1);
  const [unidadeId, setUnidadeId] = useState('');
  const [acessoMultiUnidadeSolicitado, setAcessoMultiUnidade] = useState<boolean | null>(null);
  const [dataExpiracao, setDataExpiracao] = useState('');
  const [showExpirationField, setShowExpirationField] = useState(false);
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [perfilSelecionado, setPerfilSelecionado] = useState<string>('');
  const [loadingPerfis, setLoadingPerfis] = useState(true);
  const { setBreadcrumbs } = useBreadcrumbs();
  const tipoAcessoRef = useRef<HTMLSelectElement>(null);
  const tipoAcessoContainerRef = useRef<HTMLDivElement>(null);
  const escopoAtuacaoRef = useRef<HTMLDivElement>(null);
  const [secaoAtiva, setSecaoAtiva] = useState<'tipoAcesso' | 'escopo' | null>(null);
  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    setBreadcrumbs([
      { path: '/', breadcrumb: '🏠' },
      { path: '/contas-servico/solicitar', breadcrumb: 'Solicitar Conta de Serviço' },
    ]);
  }, []);

  useEffect(() => {
    fetchUnidades();
    carregarPerfis();
  }, [fetchUnidades]);

  const carregarPerfis = async () => {
    try {
      const response = await api.get('/contas-servico/perfis-integracao');
      const perfisIntegracao = response.data.map((p: any) => ({
        id: p.id,
        nome: p.nome,
        descricao: p.descricao,
        permissoes: p.permissoes.map((perm: any) => ({ 
          id: perm.id,
          nome: perm.nome,
          descricao: perm.descricao 
        }))
      }));
      setPerfis(perfisIntegracao);
    } catch (error) {
      toast.error('Erro ao carregar perfis de integração');
    } finally {
      setLoadingPerfis(false);
    }
  };

  const getNomeFriendly = (nomePerfil: string): string => {
    const mapa: Record<string, string> = {
      'INTEGRACAO_CANDIDATOS': 'Integração de Candidatos',
      'INTEGRACAO_CANDIDATOS_COMPLETO': 'Integração Completa (Candidatos + Fotos)',
    };
    return mapa[nomePerfil] || nomePerfil;
  };

  const getDescricaoFriendly = (nomePerfil: string): string => {
    const mapa: Record<string, string> = {
      'INTEGRACAO_CANDIDATOS': 'Acesso para gerenciar candidatos e ver bancas, em processos determinados, de maneira controlada',
      'INTEGRACAO_CANDIDATOS_COMPLETO': 'Acesso completo para gerenciar candidatos, fazer upload de fotos e ver bancas, em processos determinados, de maneira controlada',
    };
    return mapa[nomePerfil] || '';
  };

  const handleProximaEtapa = (e: React.FormEvent) => {
    e.preventDefault();
    if (!unidadeId.trim()) {
      toast.error('Selecione uma unidade.');
      return;
    }
    setEtapa(2);
  };

  useEffect(() => {
    if (etapa === 2 && !loadingPerfis) {
      setSecaoAtiva('tipoAcesso');
      setTimeout(() => {
        tipoAcessoRef.current?.focus();
        tipoAcessoContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [etapa, loadingPerfis]);

  useEffect(() => {
    if (perfilSelecionado) {
      setSecaoAtiva('escopo');
    }
  }, [perfilSelecionado]);

  useEffect(() => {
    if (acessoMultiUnidadeSolicitado !== null) {
      setSecaoAtiva(null);
    }
  }, [acessoMultiUnidadeSolicitado]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!usuario) {
      toast.error('Usuário não autenticado.');
      return;
    }

    if (!perfilSelecionado) {
      toast.error('Selecione o tipo de acesso desejado.');
      return;
    }

    if (acessoMultiUnidadeSolicitado === null) {
      toast.error('Você deve escolher se a conta acessará múltiplas unidades.');
      return;
    }

    const descricao = `Conta de Serviço`;

    setEnviandoSolicitacao(true);
    try {
      await ContaServicoViewModel.solicitarContaServico({
        descricao,
        unidadeId,
        acessoMultiUnidadeSolicitado,
        perfilSolicitadoId: perfilSelecionado,
        dataExpiracaoSolicitada:
          showExpirationField && dataExpiracao
            ? new Date(dataExpiracao).toISOString()
            : undefined,
      });

      toast.success('Solicitação enviada com sucesso!');
      setSolicitacaoEnviada(true);
    } catch (err: any) {
      toast.error(BackendErrorMessage(err) || 'Erro ao enviar solicitação.');
    } finally {
      setEnviandoSolicitacao(false);
    }
  };

  return (
    <Container className="py-5" style={{ maxWidth: '800px' }}>
      <Joyride
        steps={[
          {
            target: '[data-tour="header"]',
            content: 'Nesta tela você solicita uma Conta de Serviço para integração automatizada com o sistema Identifica.',
            disableBeacon: true,
          },
          {
            target: '[data-tour="info-cards"]',
            content: 'Leia os avisos sobre responsabilidade (sua matrícula fica vinculada) e continuidade (notificação automática em caso de desligamento).',
          },
          {
            target: '[data-tour="formulario"]',
            content: 'O formulário tem 2 etapas: primeiro selecione a unidade, depois escolha o tipo de acesso (Integração de Candidatos ou Completa) e o escopo (uma ou múltiplas unidades).',
          },
          {
            target: '[data-tour="unidade"]',
            content: 'Selecione a unidade institucional que utilizará a conta. Se a unidade não estiver na lista, solicite o cadastro antes.',
          },
        ]}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        callback={(data: CallBackProps) => {
          if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) setRunTour(false);
        }}
        locale={{ back: 'Voltar', close: 'Fechar', last: 'Finalizar', next: 'Próximo', skip: 'Pular tour' }}
        styles={{ options: { primaryColor: '#2c3e50', zIndex: 10000 } }}
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <button
          className="btn btn-outline-secondary btn-sm"
          onClick={() => setRunTour(true)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}
        >
          <BsQuestionCircle /> Tour
        </button>
      </div>
      {/* Header Section */}
      <div data-tour="header" className="text-center mb-5">
        <div className="d-flex justify-content-center align-items-center mb-3">
          <BsShieldCheck size={32} className="text-primary me-3" />
          <h2 className="mb-0 fw-light" style={{ color: '#2c3e50' }}>Solicitação de Conta de Serviço</h2>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="conta-servico-tooltip">
                <strong>Conta de Serviço</strong> é uma credencial institucional utilizada para
                acesso automatizado ao sistema, vinculada a um responsável técnico mas não a uma
                pessoa física em operação. Permite a execução de processos sistêmicos com
                rastreabilidade e governança adequadas, mantendo a transparência necessária
                para operações institucionais.
              </Tooltip>
            }
          >
            <span style={{ cursor: 'pointer' }} className="ms-2">
              <BsQuestionCircle size={18} className="text-muted" />
            </span>
          </OverlayTrigger>
        </div>
        <p className="text-muted mb-0" style={{ fontSize: '1.1rem' }}>
          Solicite acesso institucional para integração com o sistema
        </p>
      </div>

      {/* Information Cards */}
      <div data-tour="info-cards" className="row mb-4">
        <div className="col-md-6 mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <div className="bg-warning bg-opacity-10 rounded-circle p-2 me-3">
                  <BsShieldCheck className="text-warning" size={20} />
                </div>
                <h6 className="mb-0 fw-semibold text-dark">Responsabilidade</h6>
              </div>
              <p className="text-muted mb-2 small">
                Ao solicitar esta conta, você se torna responsável por seu uso.
              </p>
              <ul className="small text-muted mb-0 ps-3">
                <li>Registro automático da sua matrícula</li>
                <li>Auditoria completa de todas as ações</li>
              </ul>
            </Card.Body>
          </Card>
        </div>
        <div className="col-md-6 mb-3">
          <Card className="h-100 border-0 shadow-sm">
            <Card.Body className="p-4">
              <div className="d-flex align-items-center mb-3">
                <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                  <BsQuestionCircle className="text-info" size={20} />
                </div>
                <h6 className="mb-0 fw-semibold text-dark">Continuidade</h6>
              </div>
              <p className="text-muted mb-2 small">
                Em caso de mudança de unidade ou desligamento:
              </p>
              <ul className="small text-muted mb-0 ps-3">
                <li>Notificação automática por e-mail</li>
                <li>Prazo de 7 dias para adequação</li>
              </ul>
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Form Section */}
      <Card data-tour="formulario" className="border-0 shadow-sm">
        <Card.Body className="p-5">
          {etapa === 1 ? (
            <Form onSubmit={handleProximaEtapa}>
              <div className="d-flex justify-content-center mb-4">
                <div style={{ width: '50%', height: '3px', backgroundColor: '#0d6efd' }}></div>
                <div style={{ width: '50%', height: '3px', backgroundColor: '#e9ecef' }}></div>
              </div>
              <Form.Group className="mb-4">
                <Form.Label data-tour="unidade" className="fw-semibold text-dark mb-3">Unidade Institucional *</Form.Label>
                {loadingUnidades ? (
                  <Form.Control 
                    as="select" 
                    disabled 
                    className="py-3 border-0 bg-light"
                    style={{ borderRadius: '8px' }}
                  >
                    <option>Carregando unidades...</option>
                  </Form.Control>
                ) : (
                  <Form.Select
                    value={unidadeId}
                    onChange={(e) => setUnidadeId(e.target.value)}
                    required
                    className="py-3 border-0 bg-light"
                    style={{ borderRadius: '8px', fontSize: '1rem' }}
                  >
                    <option value="">Selecione a unidade que se conectará ao Identifica</option>
                    {unidades.map((unidade) => (
                      <option key={unidade.id} value={unidade.id}>
                        {unidade.nome}
                      </option>
                    ))}
                  </Form.Select>
                )}
                {unidadeId === '' && (
                  <div className="d-flex align-items-center mt-2 text-muted" style={{ fontSize: '0.9rem' }}>
                    <BsArrowUp className="me-2" />
                    <span>Unidade não encontrada? Solicite o cadastro antes de continuar</span>
                  </div>
                )}
              </Form.Group>

              <div className="text-center mt-5">
                <Button 
                  variant="primary" 
                  type="submit" 
                  size="lg"
                  className="px-5 py-3 fw-semibold"
                  style={{ 
                    borderRadius: '8px',
                    backgroundColor: '#2c3e50',
                    borderColor: '#2c3e50',
                    fontSize: '1.1rem'
                  }}
                >
                  Próxima Etapa →
                </Button>
              </div>
            </Form>
          ) : (
            <Form onSubmit={handleSubmit}>
              <div className="d-flex justify-content-center mb-4">
                <div style={{ width: '50%', height: '3px', backgroundColor: '#198754' }}></div>
                <div style={{ width: '50%', height: '3px', backgroundColor: '#0d6efd' }}></div>
              </div>
              <Alert variant="success" className="mb-4" style={{ opacity: secaoAtiva ? 0.9 : 1, transition: 'opacity 0.3s' }}>
                <strong>Unidade selecionada:</strong> {unidades.find(u => u.id === unidadeId)?.nome}
              </Alert>

              <div className="row" ref={tipoAcessoContainerRef} style={{ 
                opacity: secaoAtiva === 'tipoAcesso' || !secaoAtiva ? 1 : 0.9, 
                transition: 'all 0.3s',
                border: secaoAtiva === 'tipoAcesso' ? '2px solid #0d6efd' : '2px solid transparent',
                borderRadius: '8px',
                padding: '16px',
                marginLeft: '0',
                marginRight: '0'
              }}>
                <div className="col-md-7">
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold text-dark mb-3">Tipo de Acesso *</Form.Label>
                    {loadingPerfis ? (
                      <div className="text-center py-3 bg-light rounded">
                        <Spinner animation="border" size="sm" className="me-2" />
                        Carregando opções...
                      </div>
                    ) : (
                      <>
                        <Form.Select
                          ref={tipoAcessoRef}
                          value={perfilSelecionado}
                          onChange={(e) => setPerfilSelecionado(e.target.value)}
                          required
                          className="py-3 border-0 bg-light"
                          style={{ borderRadius: '8px', fontSize: '1rem' }}
                        >
                          <option value="">Selecione o tipo de acesso</option>
                          {perfis.map((perfil) => (
                            <option key={perfil.id} value={perfil.id}>
                              {getNomeFriendly(perfil.nome || '')}
                            </option>
                          ))}
                        </Form.Select>
                        {perfilSelecionado && (
                          <small className="text-muted d-block mt-2">
                            {getDescricaoFriendly(perfis.find(p => p.id === perfilSelecionado)?.nome || '')}
                          </small>
                        )}
                      </>
                    )}
                  </Form.Group>
                </div>
                <div className="col-md-5">
                  {perfilSelecionado && perfis.find(p => p.id === perfilSelecionado) && (
                    <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa', borderColor: '#0d6efd !important', borderWidth: '2px' }}>
                      <div className="d-flex align-items-center mb-2">
                        <div style={{ width: '3px', height: '20px', backgroundColor: '#0d6efd', marginRight: '8px' }}></div>
                        <strong className="small">Permissões do Perfil</strong>
                      </div>
                      <ul className="small mb-0 ps-3" style={{ listStyleType: 'none' }}>
                        {perfis.find(p => p.id === perfilSelecionado)?.permissoes.map((permissao, index) => (
                          <li key={index} className="mb-1">
                            <span style={{ color: '#198754' }}>✓</span> {permissao.descricao || permissao.nome}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div style={{ 
                opacity: secaoAtiva === 'escopo' || !secaoAtiva ? 1 : 0.9, 
                transition: 'all 0.3s',
                border: secaoAtiva === 'escopo' ? '2px solid #0d6efd' : '2px solid transparent',
                borderRadius: '8px',
                padding: '16px'
              }}>
              <Form.Group className="mb-4" ref={escopoAtuacaoRef}>
                <Form.Label className="fw-semibold text-dark mb-3">Escopo de Atuação *</Form.Label>
                <p className="mb-3 text-muted small">
                  Defina se esta conta processará dados apenas da sua unidade ou também de outras unidades.
                </p>
                <style>{`
                  .access-card {
                    transition: all 0.2s ease;
                  }
                  .access-card:hover {
                    border-color: #0d6efd !important;
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                  }
                  .access-card-warning:hover {
                    border-color: #ffc107 !important;
                  }
                `}</style>
                <div className="d-flex gap-3">
                  <div
                    onClick={() => setAcessoMultiUnidade(false)}
                    className="access-card flex-fill p-3 border rounded"
                    style={{
                      cursor: 'pointer',
                      borderRadius: '8px',
                      borderWidth: '2px',
                      borderColor: acessoMultiUnidadeSolicitado === false ? '#0d6efd' : '#dee2e6',
                      backgroundColor: acessoMultiUnidadeSolicitado === false ? '#e7f1ff' : 'transparent'
                    }}
                  >
                    <div className="fw-semibold">Somente Minha Unidade</div>
                    <small className="text-muted d-block mt-1">Processar dados exclusivamente da unidade selecionada</small>
                  </div>
                  <div
                    onClick={() => setAcessoMultiUnidade(true)}
                    className="access-card access-card-warning flex-fill p-3 border rounded"
                    style={{
                      cursor: 'pointer',
                      borderRadius: '8px',
                      borderWidth: '2px',
                      borderColor: acessoMultiUnidadeSolicitado === true ? '#ffc107' : '#dee2e6',
                      backgroundColor: acessoMultiUnidadeSolicitado === true ? '#fff3cd' : 'transparent'
                    }}
                  >
                    <div className="fw-semibold">Múltiplas Unidades da Unicamp</div>
                    <small className="text-muted d-block mt-1">Processar dados que envolvem outras unidades</small>
                  </div>
                </div>
              </Form.Group>
              </div>

              {alteracaoPeriodicaDeSenhaImplementada && (
                <Form.Group className="mb-4">
                  <div className="bg-light p-4 rounded" style={{ borderRadius: '8px' }}>
                    <Form.Check
                      type="checkbox"
                      label="Configurar data de expiração"
                      checked={showExpirationField}
                      onChange={(e) => {
                        setShowExpirationField(e.target.checked);
                        if (!e.target.checked) {
                          setDataExpiracao('');
                        }
                      }}
                      className="fw-semibold"
                      style={{ fontSize: '1rem' }}
                    />
                    <Collapse in={showExpirationField}>
                      <div className="mt-3">
                        <Form.Label className="small text-muted mb-2">
                          <strong>Por padrão, a conta não expira.</strong> Configure apenas se necessário.
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={dataExpiracao}
                          onChange={(e) => setDataExpiracao(e.target.value)}
                          className="border-0 bg-white py-2"
                          style={{ borderRadius: '6px' }}
                          min={new Date().toISOString().split('T')[0]}
                        />
                      </div>
                    </Collapse>
                  </div>
                </Form.Group>
              )}

              <div className="d-flex justify-content-between mt-5" style={{ opacity: secaoAtiva ? 0.9 : 1, transition: 'opacity 0.3s' }}>
                <Button 
                  variant="outline-secondary" 
                  size="lg"
                  onClick={() => setEtapa(1)}
                  className="px-4 py-3"
                  style={{ borderRadius: '8px' }}
                >
                  ← Voltar
                </Button>
                <Button 
                  variant="primary" 
                  type="submit" 
                  size="lg"
                  disabled={enviandoSolicitacao || solicitacaoEnviada || !perfilSelecionado || acessoMultiUnidadeSolicitado === null}
                  className="px-5 py-3 fw-semibold"
                  style={{ 
                    borderRadius: '8px',
                    backgroundColor: '#2c3e50',
                    borderColor: '#2c3e50',
                    fontSize: '1.1rem'
                  }}
                >
                  {enviandoSolicitacao ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Enviando...
                    </>
                  ) : solicitacaoEnviada ? (
                    'Solicitação Enviada'
                  ) : (
                    'Enviar Solicitação'
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default SolicitarContaServicoPage;
